import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { Review } from '../../../models/review';
import { ReplyTemplate } from '../reply-tab-group/template-selection/template-selection.component';
import { ReviewExportFormatChoice } from '../review-export-popup/review-export-popup.component';
import { ReviewsResponse } from '../../../models/review-response';
import { AiSettings, AiSettingsReviewType } from '../reply-tab-group/ai-replies-settings/ai-replies-settings.component';
import { AiRepliesLanguage } from './review.state';
import { AiAssistedReply } from '../../../models/ai-assisted-reply';
import { ReviewFilters } from '../../../models/review-filters';
import { ReviewTranslation } from '../../../models/review-translation';
import { AnsweringAction } from '../answering-action';

export const ReviewsActions = createActionGroup({
  source: 'REVIEWS',
  events: {
    getReviews: emptyProps(),
    getReviewsSuccess: props<{ reviewsResponse: ReviewsResponse }>(),
    removeReview: props<{ id: number }>(),
    garbageCollectStaleData: props<{
      translationsToRemove?: string[],
      repliesToRemove?: number[]
    }>(),
  }
});

export const ReviewActions = createActionGroup({
  source: 'REVIEWS - SINGLE REVIEW OPERATION',
  events: {
    answerReviewReply: props<{ id: number,
      replyText: string,
      gbpLocationId: string,
      currentAnsweringAction: AnsweringAction }>(), // request
    saveReviewSuccess: props<{ review: Review }>(), // store update
    reportReview: props<{ review: Review }>(),
    translateReview: props<{ reviewId: number, languageCode: string }>(),
    translateReviewSuccess: props<{ reviewTranslation: ReviewTranslation }>(),
    translateReviewError: props<{ reviewId: number }>(),
  }
});

export const ReviewSyncActions = createActionGroup({
  source: 'REVIEWS - SYNC OPERATION',
  events: {
    syncReviewFilters: props<{ reviewFilters: ReviewFilters, preventReviewUpdate?: boolean }>(),
    syncReviewPaginatorData: props<{ pageSize: number,
      pageIndex: number,
      totalNumber?: number,
      preventReviewUpdate?: boolean,
    }>(),
  }
});

export const ReplyTemplatesActions = createActionGroup({
  source: 'REVIEWS - REPLY TEMPLATES',
  events: {
    getReplyTemplates: emptyProps(),
    getReplyTemplatesSuccess: props<{ replyTemplates: ReplyTemplate[] }>(),
    upsertReplyTemplate: props<{ replyTemplate: ReplyTemplate }>(),
    deleteReplyTemplate: props<{ id: number }>(),
  }
});

export const AiReplyActions = createActionGroup({
  source: 'REVIEWS - AI-ASSISTED REPLIES',
  events: {
    getAiAssistedReply: props<{ review: Review }>(),
    getAiAssistedReplySuccess: props<{ aiAssistedReply: AiAssistedReply, id: number }>(),
    getAiAssistedReplyError: emptyProps(),
    sendForAnalysis: props<{
      replyText: string,
      aiAssistedReplyText: string,
      reviewId: number,
      gbpLocationId: string,
      aiAssistedRequestId: string
    }>(),
    sendForAnalysisSuccess: emptyProps(),
  }
});

export const AiSettingsActions = createActionGroup({
  source: 'REVIEWS - AI-ASSISTED REPLIES SETTINGS',
  events: {
    getAiSettings: emptyProps(),
    getAiSettingsSuccess: props<{aiSettings: AiSettings[]}>(),
    getAiSettingsError: emptyProps(),
    saveAiSettings: props<{aiSettings: AiSettings}>(),
    saveAiSettingsSuccess: props<{aiSettings: AiSettings}>(),
    saveAiSettingsError: emptyProps(),
    deleteAiSettings: props<{id: number}>(),
    deleteAiSettingsSuccess: props<{id: number}>(),
    deleteAiSettingsError: emptyProps(),
    selectAiSettings: props<{aiSettings: AiSettings}>(),
    selectAiSettingsSuccess: props<{aiSettings: AiSettings}>(),
    unSelectAiSettings: props<{aiSettings: AiSettings}>(),
    unSelectAiSettingsSuccess: props<{aiSettings: AiSettings}>(),
    getDefaultLanguages: emptyProps(),
    getDefaultLanguagesSuccess: props<{defaultLanguages: AiRepliesLanguage[]}>(), // list of language codes, i.e. ['EN', 'DE'], etc.
    updateDefaultLanguage: props<{ defaultLanguage: string, reviewType: AiSettingsReviewType }>(), // // language code, i.e. 'EN', 'DE', etc.
    updateDefaultLanguageSuccess: props<{aiSettings: AiSettings}>(),
    updateReplyStyle: props<{style: string}>(),
    updateReplyStyleSuccess: props<{aiSettings: AiSettings}>(),
    updateReplyStyleError: emptyProps(),
  }
});


export const ExportReviewsActions = createActionGroup({
  source: 'REVIEWS - EXPORT REVIEWS',
  events: {
    exportReviews: props<{ format: ReviewExportFormatChoice, filtered: boolean, }>(),
    exportReviewsSuccess: emptyProps(),
    exportReviewsFail: emptyProps(),
  }
});

export const ExportReplyTemplatesActions = createActionGroup({
  source: 'REVIEWS - EXPORT REPLY TEMPLATES',
  events: {
    exportReplyTemplates: emptyProps(),
    exportReplyTemplatesSuccess: emptyProps(),
    exportReplyTemplatesFail: emptyProps(),
  }
});

export const cancelExport = createAction('[REVIEWS] Cancel export');

export const handleBeError = createAction('[REVIEWS] Show Error Snackbar',
    props<{
      error: HttpErrorResponse,
      userFriendlyMessage: string,
      actionButtons?: { text: string, action: Function }[],
    }>());
export const reviewComponentDestroyed = createAction('[REVIEWS] Component destroyed');
